import React, {
  FC,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useLevelController } from "../Engine/Commands/LevelController";
import { GLTFModel } from "../Engine/GLTFModel";
import { DEV_MODE, MAP_MODE, PERF_MODE, PERFORMANCE } from "../Engine/DevConstants";
import { MapControls } from "@react-three/drei";
import { Physics } from "@react-three/rapier";
import { Canvas } from "@react-three/fiber";
import { Vector3 } from "three";
import {
  SceneContextProvider,
  SceneContextType,
} from "../Engine/Features/Environmental/SceneController";
import { AdaptiveShadows, getOptimalShadowMapSize } from "../Engine/Features/Shadows/AdaptiveShadows";

const { Perf = undefined } =
  process.env.NODE_ENV === "development" ? require("r3f-perf") : {};

const SUN_POSITION = new Vector3(15, 25, 25);

export const Game: FC = () => {
  const [skyColor, setSkyColor] = React.useState<string>("lightblue");
  const [shadowMapSize, setShadowMapSize] = useState<number[]>(getOptimalShadowMapSize());
  const sceneContext = useMemo<SceneContextType>(
    () => ({ setSkyColor }),
    [setSkyColor]
  );
  const { levelUrl } = useLevelController();
  
  const onCanvasClick = useCallback(({ currentTarget }: ReactMouseEvent) => {
    if (!MAP_MODE) {
      currentTarget.requestPointerLock();

      if (currentTarget.requestFullscreen) {
        currentTarget.requestFullscreen().then(() => undefined);
      }
    }
  }, []);

  return (
    <SceneContextProvider value={sceneContext}>
      <Canvas
        camera={{
          position: [0, 1, 3],
          rotation: [0.05, 0, 0],
          fov: 80,
          near: 0.01,
          far: 1000,
        }}
        shadows="soft"
        onClick={onCanvasClick}
        style={{
          backgroundColor: skyColor,
        }}
        gl={{ 
          antialias: true,
          powerPreference: "high-performance",
          alpha: false,
          stencil: false
        }}
        dpr={[1, 2]}
        performance={{ min: 0.5 }}
      >
        {MAP_MODE ? <MapControls /> : undefined}
        {PERF_MODE && Perf ? <Perf /> : undefined}
        <AdaptiveShadows 
          targetFPS={60}
          onShadowQualityChange={setShadowMapSize}
        />
        <ambientLight intensity={0.25} />
        <directionalLight
          intensity={1}
          position={SUN_POSITION}
          castShadow={true}
          shadow-camera-near={0.1}
          shadow-camera-far={1000}
          shadow-camera-left={-100}
          shadow-camera-right={100}
          shadow-camera-top={100}
          shadow-camera-bottom={-100}
          shadow-bias={-0.00009999}
          shadow-mapSize={shadowMapSize}
          shadow-radius={3}
        />
        <Physics 
          debug={DEV_MODE} 
          gravity={[0, -9.8 * 2, 0]} 
          erp={0.8} 
          updateLoop='independent'
          timeStep={1/60}
          maxStabilizationIterations={4}
          maxVelocityIterations={8}>
          <GLTFModel key={levelUrl} url={levelUrl} />
        </Physics>
      </Canvas>
    </SceneContextProvider>
  );
};
